<template>
  <div class="container mx-auto px-4 py-10" v-if="event">
    <div class="flex flex-col md:flex-row gap-8">
      <!-- Left Column -->
      <div class="md:w-2/3">
        <!-- Event Images -->
        <div class="grid grid-cols-3 gap-4 mb-8 md:border-r md:border-dark md:pr-16">
          <PinterestShareWrapper class="col-span-2 w-full" :image="event.photo1" :description="event.event_name">
            <NuxtImg :src="event.photo1" :alt="`Event image 1`" class="w-full object-cover aspect-square"/>
          </PinterestShareWrapper>
          <div class="grid grid-rows-2 gap-4">
            <PinterestShareWrapper class="w-full" :image="event.photo2" :description="event.event_name">
              <NuxtImg :src="event.photo2" :alt="`Event image 2`" class="w-full object-cover aspect-square"/>
            </PinterestShareWrapper>
            <PinterestShareWrapper class="w-full" :image="event.photo3" :description="event.event_name">
              <NuxtImg :src="event.photo3" :alt="`Event image 3`" class="w-full object-cover aspect-square"/>
            </PinterestShareWrapper>
          </div>
        </div>
        
        <!-- Right Column Content for Mobile -->
        <div class="md:hidden mb-8">
          <span
            class="inline-flex items-center gap-2 mb-10 cursor-pointer underline"
            @click="openInviteFriendModal"
          >
            <WellIcon icon="upload-01" />
            SHARE
          </span>
          <h1 class="text-3xl mb-4">{{ event.event_name }}</h1>
          <div class="mb-4">
            <InstructorsStack :instructors="event.instructors" size="24px" :clickable="true"/>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-6">
            <div class="flex items-center">
              <EventDate :date="event.dateShort" />
            </div>
            <div class="flex items-center">
              <EventLocation :event="event" />
            </div>
            <div class="flex items-center">
              <EventTime :time="event.time" />
            </div>
            <div class="flex items-center">
              <EventPrice :price="event.www_price" />
            </div>
            <div class="flex items-center">
              <EventDuration :duration="event.duration" />
            </div>
          </div>
          <div 
            v-if="isAlreadyRegistered"
            class="flex flex-col md:flex-row gap-4"
          >
            <a
              class="button-light-outlined w-full text-center md:text-left"
              :href="event.event_url"
            >
              GO TO EVENT
            </a>
            <div class="w-full">
              <button
                class="button-dark-outlined w-full text-center md:text-left"
                @click="addToCalendarPopover.toggle"
              >
                ADD TO CALENDAR
              </button>
              <Popover
                ref="addToCalendarPopover"
              >
                <div class="flex flex-col overflow-hidden rounded-md gap-2 p-4">
                  <ul>
                    <li 
                      class="cursor-pointer hover:bg-dark hover:text-white rounded-md p-2"
                      @click="addToGoogleCalendar"
                    >
                      Add to Google Calendar
                    </li>
                    <li 
                      class="cursor-pointer hover:bg-dark hover:text-white rounded-md p-2"
                    >
                      <a :href="iCalLink" target="_blank">
                        Download iCal File
                      </a>
                    </li>
                  </ul>
                </div>
              </Popover>
            </div>
          </div>
          <div v-else>
            <div v-if="event.isExpired && !event.isPassedEventRecordingAvailable">
              <div class="bg-dark text-white text-sm px-3 py-1 rounded-md mt-5 inline-block shadow-md">
                • EVENT IS NO LONGER AVAILABLE •
              </div>
            </div>
            <button
              v-else-if="!event.soldOut"
              class="button-dark-outlined w-full"
              @click="openCheckoutModal"
            >
              <span v-if="event.isPassedEventRecordingAvailable">
                WATCH REPLAY
              </span>
              <span v-else>
                REGISTER NOW
              </span>
            </button>
            <span
              v-else
              class="bg-dark text-white text-sm px-3 py-1 rounded-md mt-5 inline-block shadow-md"
            >
              • SOLD OUT •
            </span>    
            <div v-if="event.isExpired && event.next_event_id">
              <NuxtLink 
                :to="`/events/${getEventSlug(event.next_event_id, event.event_name)}`"
                class="text-primary underline mt-5 block"
              >
                View Next Available Date
              </NuxtLink>
            </div>
          </div>
        </div>
        
        <!-- Event Description -->
        <div v-if="event.event_description_new" class="prose max-w-none mt-10">
          <h2 class="text-4.5xl mb-6">About This Event</h2>
          <div class="mt-4 event-description" data-nosnippet v-html="event.event_description_new">
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="hidden md:block md:w-1/3">
        <div class="sticky top-8 ml-2 md:ml-8">
          <span
            class="inline-flex items-center gap-2 mb-10 cursor-pointer underline"
            @click="openInviteFriendModal"
          >
            <WellIcon icon="upload-01" />
            INVITE A FRIEND
          </span>
          <h1
            class="text-4.5xl mb-4"
            @click="openAdminReviewModal"
          >
            {{ event.event_name }}
          </h1>
          <div class="mb-10 flex">
            <InstructorsStack :instructors="event.instructors" size="24px" :clickable="true"/>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4 mb-10">
            <div class="flex items-center">
              <EventDate :date="event.dateShort" @click="openToggleFeaturedModal" />
            </div>
            <div class="flex items-center">
              <EventLocation :event="event" />
            </div>
            <div class="flex items-center">
              <EventTime :time="event.time" />
            </div>
            <div class="flex items-center">
              <EventPrice :price="event.www_price" />
            </div>
            <div class="flex items-center">
              <EventDuration :duration="event.duration" />
            </div>
          </div>
          <div 
            v-if="isAlreadyRegistered"
            class="flex flex-col 2xl:flex-row gap-4"
          >
            <a
              class="button-light-outlined w-full text-center"
              :href="event.event_url"
            >
              GO TO EVENT
            </a>
            <div class="w-full">
              <button
                class="button-dark-outlined w-full text-center"
                @click="addToCalendarPopover.toggle"
              >
                ADD TO CALENDAR
              </button>
              <Popover
                ref="addToCalendarPopover"
              >
                <div class="flex flex-col overflow-hidden rounded-md gap-2 p-4">
                  <ul>
                    <li 
                      class="cursor-pointer hover:bg-dark hover:text-white rounded-md p-2"
                      @click="addToGoogleCalendar"
                    >
                      Add to Google Calendar
                    </li>
                    <li 
                      class="cursor-pointer hover:bg-dark hover:text-white rounded-md p-2"
                    >
                      <a :href="iCalLink" target="_blank">
                        Download iCal File
                      </a>
                    </li>
                  </ul>
                </div>
              </Popover>
            </div>
          </div>
          <div v-else>
            <div v-if="event.isExpired && !event.isPassedEventRecordingAvailable">
              <div 
                class="bg-dark text-white text-sm px-3 py-1 rounded-md mt-5 inline-block shadow-md"
              >
                • EVENT IS NO LONGER AVAILABLE •  
              </div>
            </div>
            <button
              v-else-if="!event.soldOut"
              class="button-dark-outlined"
              @click="openCheckoutModal"
            >
              <span v-if="event.isPassedEventRecordingAvailable">
                WATCH REPLAY
              </span>
              <span v-else>
                REGISTER NOW
              </span>
            </button>
            <span
              v-else
              class="bg-dark text-white text-sm px-3 py-1 rounded-md mt-5 inline-block shadow-md"
            >
              • SOLD OUT •
            </span>
            <div v-if="event.isExpired && event.next_event_id">
              <NuxtLink 
                :to="`/events/${getEventSlug(event.next_event_id, event.event_name)}`"
                class="text-primary underline mt-5 block"
              >
                View Next Available Date
              </NuxtLink>
            </div>
          </div>            
        </div>
      </div>
    </div>
    <div 
      v-if="similarEvents.length > 0"
      class="mt-10"
    >
      <hr class="my-10 border-dark"/>
      <section class="mt-5">

        <EventsSlider
          :events="similarEvents"
          :inContainer="true"
        >
          <template #title>
            <span class="inline-flex gap-2 items-center">
              <WellIcon icon="lightbulb-05" />
              SIMILAR EXPERIENCES
            </span>    
          </template>
        </EventsSlider>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useApi } from '@/composables/useApi'
import { getAddToGoogleCalendarLink, getEventDetails } from '~/utils/eventDetails';
import { useServerSeoMeta } from '#imports'
import EventDate from '@/components/events/EventDate.vue';
import EventTime from '@/components/events/EventTime.vue';
import EventDuration from '@/components/events/EventDuration.vue';
import EventPrice from '@/components/events/EventPrice.vue';
import EventLocation from '@/components/events/EventLocation.vue';
import InstructorsStack from '@/components/events/InstructorsStack.vue';
import WellIcon from '@/components/common/WellIcon.vue';
import EventCheckoutModal from '@/components/events/EventCheckoutModal.vue';
import EventAdminReviewModel from '@/components/events/EventAdminReviewModel.vue';
import EventShareModal from '@/components/events/EventShareModal.vue';
import EventToggleFeaturedModel from '@/components/events/EventToggleFeaturedModel.vue';
import { getEventLd } from '@/utils/eventDetails'
import { useModal } from '@/composables/useModal' // Make sure this import is present
import { useUserStore} from '@/stores/user'
import getEventSlug from '@/utils/getEventSlug'
import PinterestShareWrapper from '@/components/common/PinterestShareWrapper.vue';
const config = useRuntimeConfig()
const baseUrl = config.public.BASE_API_URL

const route = useRoute()
const modal = useModal()
const { fetchEvent, getEvent, fetchSimilarEvents } = useApi()

const event = ref<EventDetails | null>(null)
const similarEvents = ref<EventResponse[] | []>([])
const addToCalendarPopover = ref()
const reviewClickCount = ref(0)
const toggleFeaturedClickCount = ref(0)

const eventId = parseInt(route.params.event_id.toString().split('-').pop() as string)

const { data, error } = await fetchEvent(eventId)
if (error.value) {
  console.error('Error fetching event:', error.value)
  throw createError({
    statusCode: 404,
    statusMessage: "Event not found",
  })
} else {
  event.value = getEventDetails(data.value)
}

if (process.server && event.value?.next_event_id) {
  if(event.value?.isExpired && !event.value?.isPassedEventRecordingAvailable) {
    navigateTo(`/events/${getEventSlug(event.value.next_event_id, event.value.event_name)}`, { redirectCode: 301 })
  }
}

if (process.server) {
  useServerSeoMeta({
    title: () => event.value?.event_name || 'Event Details',
    ogTitle: () => event.value?.event_name || 'Event Details',
    description: () => event.value?.event_description_text || 'Event details page',
    ogDescription: () => event.value?.event_description_text || 'Event details page',
    ogImage: () => event.value?.photo1 || '',
    twitterCard: 'summary_large_image',
    twitterTitle: () => event.value?.event_name || 'Event Details',
    twitterDescription: () => event.value?.event_description_text || 'Event details page',
    twitterImage: () => event.value?.photo1 || '',
  })

  const eventLd = getEventLd(event.value)
  const url = useRequestURL()

  useHead({
    link: [
      {
        rel: 'canonical',
        href: `${url.origin}/events/${event.value?.slug}`
      }
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(eventLd)
      }
    ]
  })
} else {
  useHead({
    title: () => event.value?.event_name || 'Event Details',
  })
}


onMounted(async () => {
  similarEvents.value = await fetchSimilarEvents(eventId)
})

const openCheckoutModal = () => {  
  modal.show(EventCheckoutModal, {
    event: event.value,
    similarEvents: similarEvents.value,
    onPurchase: async () => {
      // Reload the event when the modal is closed
      const { data, error } = await fetchEvent(eventId)
      if (error.value) {
        console.error('Error reloading event:', error.value)
      } else {
        event.value = getEventDetails(data.value)
      }
    }
  })
}

const openAdminReviewModal = () => {
  reviewClickCount.value++
  if (reviewClickCount.value > 5 && isLoggedIn.value) {
    modal.show(EventAdminReviewModel, {
      event: event.value,
    })
  }
}

const openToggleFeaturedModal = () => {
  toggleFeaturedClickCount.value++
  if (toggleFeaturedClickCount.value > 5 && isLoggedIn.value) {
    modal.show(EventToggleFeaturedModel, {
      event: event.value,
    })
  }
}

const openInviteFriendModal = () => {
  modal.show(EventShareModal, {
    event: event.value,
  })
}

const isAlreadyRegistered = computed(() => {
  return event.value?.is_registered
})

const isInPerson = computed(() => {
  return event.value?.event_type === 'studio_event'
})

const addToGoogleCalendar = () => {
  window.open(getAddToGoogleCalendarLink(event.value), '_blank')
}

const iCalLink = computed(() => `${baseUrl}/api/studios/${event.value.studio_url}/events/${event.value.id}/ical`)

const { isLoggedIn } = storeToRefs(useUserStore())
watch(isLoggedIn, async (isLoggedIn) => {
  try {
    event.value = await getEvent(eventId)
  } catch (error) {
    console.error('Error reloading event:', error)
  }
})
</script>

<style scoped>
/* Remove the media query for sticky positioning */
.event-description {
  :deep(h3) {
    font-size: 1.25rem !important;
    margin-bottom: 0.5rem;
  }
  :deep(p) {
    margin-bottom: 1rem;
  }
  :deep(ul) {
    list-style-type: disc;
    margin-bottom: 1rem;
    margin-left: 1.25rem;
  }

  :deep(ul li) {
    padding-left: 0.2rem;
  }
  
  :deep(ol) {
    list-style-type: decimal;
    margin-left: 1.25rem;
    margin-bottom: 1rem;
  }
  :deep(ol li) {
    padding-left: 0.2rem;
  }
}
</style>
